import { SparklesIcon } from 'lucide-react';
import type { Snap } from '@/types/database.types';
import BaseSnapCard from './BaseSnapCard';

interface InfluencerSnapCardProps {
  snap: Snap & { activity: 'hot' | 'trending' | null };
  onDelete: (id: string) => void;
  onLike: (id: string, event: React.MouseEvent) => void;
  onVote?: (snapId: string, optionId: string) => void;
  onRemoveVote?: (snapId: string) => void;
  isVoting?: boolean;
}

export default function InfluencerSnapCard({ snap, onDelete, onLike, onVote, onRemoveVote, isVoting }: InfluencerSnapCardProps) {
  // Determine tier based on follower count
  const getInfluencerStyle = () => {
    const followerCount = snap.creator?.followers_count || 0;
    
    if (followerCount >= 1_000_000) {
      return {
        className: "bg-gradient-to-br from-blue-50 to-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden cursor-pointer relative border-2 border-blue-200 h-full flex flex-col",
        glowStyle: `
          @keyframes diamond-glow {
            0% { background-position: 0% 50%; }
            100% { background-position: 200% 50%; }
          }
          .diamond-glow::before {
            content: '';
            position: absolute;
            top: -4px;
            left: 0;
            width: 100%;
            height: 6px;
            background: linear-gradient(90deg, #00bfff, #1e90ff, #87ceeb, #b0e0e6, #87ceeb, #1e90ff, #00bfff);
            background-size: 200% 100%;
            box-shadow: 0 0 25px rgba(0, 191, 255, 0.8), 0 0 30px rgba(30, 144, 255, 0.7);
            border-radius: 12px 12px 0 0;
            animation: diamond-glow 2.5s linear infinite;
            filter: blur(4px);
            opacity: 0.95;
          }
        `,
        badge: "Diamond"
      };
    } else if (followerCount >= 100_000) {
      return {
        className: "bg-gradient-to-br from-yellow-50 to-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden cursor-pointer relative border-2 border-yellow-200 h-full flex flex-col",
        glowStyle: `
          @keyframes gold-glow {
            0% { background-position: 0% 50%; }
            100% { background-position: 200% 50%; }
          }
          .gold-glow::before {
            content: '';
            position: absolute;
            top: -4px;
            left: 0;
            width: 100%;
            height: 6px;
            background: linear-gradient(90deg, #ffd700, #daa520, #ffd700, #f0e68c, #ffd700, #daa520, #ffd700);
            background-size: 200% 100%;
            box-shadow: 0 0 25px rgba(255, 215, 0, 0.8), 0 0 30px rgba(218, 165, 32, 0.7);
            border-radius: 12px 12px 0 0;
            animation: gold-glow 2.5s linear infinite;
            filter: blur(4px);
            opacity: 0.95;
          }
        `,
        badge: "Gold"
      };
    } else if (followerCount >= 10_000) {
      return {
        className: "bg-gradient-to-br from-gray-50 to-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden cursor-pointer relative border-2 border-gray-200 h-full flex flex-col",
        glowStyle: `
          @keyframes silver-glow {
            0% { background-position: 0% 50%; }
            100% { background-position: 200% 50%; }
          }
          .silver-glow::before {
            content: '';
            position: absolute;
            top: -4px;
            left: 0;
            width: 100%;
            height: 6px;
            background: linear-gradient(90deg, #c0c0c0, #e8e8e8, #ffffff, #e8e8e8, #c0c0c0);
            background-size: 200% 100%;
            box-shadow: 0 0 25px rgba(192, 192, 192, 0.8), 0 0 30px rgba(232, 232, 232, 0.7);
            border-radius: 12px 12px 0 0;
            animation: silver-glow 2.5s linear infinite;
            filter: blur(4px);
            opacity: 0.95;
          }
        `,
        badge: "Silver"
      };
    } else if (followerCount >= 1_000) {
      return {
        className: "bg-gradient-to-br from-amber-50 to-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden cursor-pointer relative border-2 border-amber-200 h-full flex flex-col",
        glowStyle: `
          @keyframes bronze-glow {
            0% { background-position: 0% 50%; }
            100% { background-position: 200% 50%; }
          }
          .bronze-glow::before {
            content: '';
            position: absolute;
            top: -4px;
            left: 0;
            width: 100%;
            height: 6px;
            background: linear-gradient(90deg, #cd7f32, #b87333, #cd7f32, #daa520, #cd7f32, #b87333, #cd7f32);
            background-size: 200% 100%;
            box-shadow: 0 0 25px rgba(205, 127, 50, 0.8), 0 0 30px rgba(184, 115, 51, 0.7);
            border-radius: 12px 12px 0 0;
            animation: bronze-glow 2.5s linear infinite;
            filter: blur(4px);
            opacity: 0.95;
          }
        `,
        badge: "Bronze"
      };
    } else {
      return {
        className: "bg-gradient-to-br from-white/25 to-white/15 backdrop-blur-sm border border-white/30 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden h-full flex flex-col",
        glowStyle: '',
        badge: "Influencer"
      };
    }
  };

  const style = getInfluencerStyle();
  const followerCount = snap.creator?.followers_count || 0;

  return (
    <>
      {style.glowStyle && <style>{style.glowStyle}</style>}
      <BaseSnapCard
        snap={snap}
        onDelete={onDelete}
        onLike={onLike}
        onVote={onVote}
        onRemoveVote={onRemoveVote}
        isVoting={isVoting}
        wrapperClassName={`${style.className} ${followerCount >= 1_000 ? style.badge.toLowerCase() + '-glow' : ''}`}
        badges={
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
            <SparklesIcon className="h-3.5 w-3.5 mr-1" />
            {style.badge}
          </span>
        }
      />
    </>
  );
}